/*
  Product-card quick add with Amazon-style quantity stepper
  (snippets/bbb-quick-add.liquid).

  Each single-variant card renders a `.bbb-quick-add[data-bbb-add][data-variant-id]`
  wrapper with a product <form> containing a "+" submit button and a hidden
  quantity stepper. This script:

  - On load, reads the cart once and shows the stepper (with the current line
    quantity) for any variant already in the cart, so the state persists across
    reloads.
  - On "+": AJAX-adds one, opens the cart drawer immediately (no wait on the
    count fetch), and swaps the control to the full-width stepper.
  - On stepper +/−: optimistically updates the number, then debounces a
    /cart/change.js call to set the absolute line quantity, re-rendering the
    drawer body and refreshing the header count. Reaching 0 removes the line and
    reverts to the "+" button.

  One delegated submit + click listener on document, attached once. The wrapper
  does NOT use the theme's `.quick-add-wrapper.is-singular` classes, so the
  shared QuickAdd handler (assets/theme.js) never binds these forms.
*/
(() => {
  if (window.__bbbQuickAddInit) return;
  window.__bbbQuickAddInit = true;

  const WRAP = '.bbb-quick-add[data-bbb-add]';
  const CHANGE_DEBOUNCE_MS = 400;
  const timers = {}; // variantId -> debounce timer

  const ICON =
    '<svg class="bbb-quick-add__icon" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" aria-hidden="true" focusable="false"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>';
  const SPINNER =
    '<svg class="bbb-quick-add__icon bbb-quick-add__icon--spin" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" aria-hidden="true" focusable="false"><path d="M12 2a10 10 0 0 1 10 10"></path></svg>';

  function isDrawer() {
    const pc = document.getElementById('PageContainer');
    return !pc || pc.dataset.cartType !== 'page';
  }

  function wrappersFor(variantId) {
    return [...document.querySelectorAll(`${WRAP}[data-variant-id="${variantId}"]`)];
  }

  function setQty(wrapper, qty) {
    qty = Math.max(0, qty | 0);
    wrapper.dataset.qty = String(qty);
    const label = wrapper.querySelector('[data-bbb-qty]');
    if (label) label.textContent = String(qty);
    wrapper.classList.toggle('is-in-cart', qty > 0);
  }

  function setAllQty(variantId, qty) {
    wrappersFor(variantId).forEach((w) => setQty(w, qty));
  }

  function currentQty(wrapper) {
    return parseInt(wrapper.dataset.qty || '0', 10) || 0;
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    return { ok: res.ok, json };
  }

  async function refreshCount() {
    try {
      const url = (window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '?view=compare';
      const res = await fetch(url);
      const cart = await res.json();
      if (window.wetheme && typeof window.wetheme.updateCartCount === 'function') {
        window.wetheme.updateCartCount(cart);
      }
    } catch (e) {
      /* non-blocking */
    }
  }

  function announce(wrapper) {
    const live = wrapper.querySelector('.sr-only[aria-live]');
    const msg = wrapper.getAttribute('data-product-added');
    if (live && msg) live.textContent = msg;
  }

  /* ---- Initial add (from the "+" submit) ---- */
  async function add(wrapper) {
    const variantId = wrapper.dataset.variantId;
    if (!variantId || wrapper.dataset.submitting === 'true') return;
    wrapper.dataset.submitting = 'true';

    const btn = wrapper.querySelector('.bbb-quick-add__btn');
    if (btn) {
      btn.classList.add('is-loading');
      btn.innerHTML = SPINNER;
      btn.setAttribute('aria-busy', 'true');
    }

    const drawer = isDrawer();
    const body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (drawer) body.sections = 'cart-drawer';

    try {
      const { ok, json } = await postJSON('/cart/add.js', body);
      if (!ok) {
        if (btn) {
          btn.classList.remove('is-loading');
          btn.innerHTML = ICON;
          btn.removeAttribute('aria-busy');
          btn.setAttribute('title', json.description || 'Could not add to cart.');
        }
        if (drawer) window.eventBus.emit('render:cart:drawer');
        return;
      }
      setAllQty(variantId, currentQty(wrapper) + 1);
      if (drawer) {
        window.eventBus.emit('update:cart:drawer', json);
        window.eventBus.emit('open:cart:drawer', { scrollToTop: true });
      }
      announce(wrapper);
      refreshCount();
      if (btn) {
        btn.classList.remove('is-loading');
        btn.innerHTML = ICON;
        btn.removeAttribute('aria-busy');
      }
    } catch (err) {
      console.error('Quick add failed:', err);
      if (btn) {
        btn.classList.remove('is-loading');
        btn.innerHTML = ICON;
        btn.removeAttribute('aria-busy');
      }
    } finally {
      wrapper.dataset.submitting = 'false';
    }
  }

  /* ---- Stepper +/− (debounced absolute-quantity change) ---- */
  function step(wrapper, delta) {
    const variantId = wrapper.dataset.variantId;
    if (!variantId) return;
    const target = Math.max(0, currentQty(wrapper) + delta);
    setAllQty(variantId, target); // optimistic

    clearTimeout(timers[variantId]);
    timers[variantId] = setTimeout(() => commitChange(variantId, target), CHANGE_DEBOUNCE_MS);
  }

  async function commitChange(variantId, quantity) {
    const drawer = isDrawer();
    // /cart/update.js sets an absolute quantity keyed by variant id (0 removes
    // the line). More robust than change.js, which 400s on a bare variant id.
    const body = { updates: { [String(variantId)]: quantity } };
    if (drawer) body.sections = 'cart-drawer';
    try {
      const { ok, json } = await postJSON('/cart/update.js', body);
      if (!ok) {
        if (drawer) window.eventBus.emit('render:cart:drawer');
        return;
      }
      // Reconcile from the authoritative cart in the response.
      const line = (json.items || []).find((i) => String(i.variant_id) === String(variantId));
      setAllQty(variantId, line ? line.quantity : 0);
      if (drawer) window.eventBus.emit('update:cart:drawer', json);
      refreshCount();
    } catch (e) {
      if (drawer) window.eventBus.emit('render:cart:drawer');
    }
  }

  /* ---- Delegated events ---- */
  function onSubmit(e) {
    const wrapper = e.target.closest(WRAP);
    if (!wrapper) return;
    e.preventDefault();
    if (!wrapper.classList.contains('is-in-cart')) add(wrapper);
  }

  function onClick(e) {
    const inc = e.target.closest('[data-bbb-inc]');
    const dec = e.target.closest('[data-bbb-dec]');
    if (!inc && !dec) return;
    const wrapper = e.target.closest(WRAP);
    if (!wrapper) return;
    e.preventDefault();
    step(wrapper, inc ? 1 : -1);
  }

  /* ---- Initial sync from cart ---- */
  async function syncFromCart() {
    const wraps = document.querySelectorAll(WRAP);
    if (!wraps.length) return;
    try {
      const res = await fetch((window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart') + '.js');
      const cart = await res.json();
      const qtyByVariant = {};
      (cart.items || []).forEach((i) => {
        qtyByVariant[i.variant_id] = (qtyByVariant[i.variant_id] || 0) + i.quantity;
      });
      wraps.forEach((w) => setQty(w, qtyByVariant[w.dataset.variantId] || 0));
    } catch (e) {
      /* leave all in the default "+" state */
    }
  }

  document.addEventListener('submit', onSubmit);
  document.addEventListener('click', onClick);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncFromCart);
  } else {
    syncFromCart();
  }
})();
